import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getGetAgentChatThreadQueryKey,
  getListAgentChatThreadsQueryKey,
  useDeleteAgentChatThread,
  useToggleAgentChatThreadFavorite,
  useUpdateAgentChatThread,
} from '@/entities/project/api/generated/api';
import { invalidateByPathPrefix } from '@/shared/lib/query/invalidateByPathPrefix';
import { useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { Dialog, Text, toast } from '@/shared/ui';
import { Input } from '@/shared/ui/form/input/Input';

type ThreadRef = { id: string; title: string };

type Options = {
  projectId: string;
  /** 削除成功時のコールバック。例: チャット詳細ヘッダーで削除後にパネルを閉じるなど */
  onDeleteSuccess?: (threadId: string) => void;
};

type Result = {
  openRename: (thread: ThreadRef) => void;
  openDelete: (thread: ThreadRef) => void;
  toggleFavorite: (thread: { id: string; is_favorite: boolean }) => void;
  copyUrl: (threadId: string) => Promise<void>;
  /** 削除・名前変更ダイアログ。呼び出し元の JSX 末尾でレンダリングする */
  dialogs: ReactNode;
};

/**
 * AI チャットスレッドに対する共通操作（削除・名前変更・お気に入りトグル・URL コピー）。
 *
 * `ChatHistoryList`（履歴一覧側の3点メニュー）と `ProjectDetailAIChatHeader`
 *（チャット詳細ヘッダーの3点メニュー）で同じロジック・UI を再利用する目的で抽出した。
 */
export const useThreadActions = ({ projectId, onDeleteSuccess }: Options): Result => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [threadToDelete, setThreadToDelete] = useState<ThreadRef | null>(null);
  const [threadToRename, setThreadToRename] = useState<ThreadRef | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // メモは `project_agent_chat_threads` でスレッドを参照しているため、
  // スレッドの削除・タイトル変更時にメモの一覧・詳細キャッシュも無効化して、
  // 削除済みスレッドの行が残ったり、古いタイトルが表示されたままにならないようにする。
  const invalidateRelatedMemos = () => {
    invalidateByPathPrefix(queryClient, { include: [`/projects/${projectId}/memos`] });
  };

  const deleteMutation = useDeleteAgentChatThread({
    mutation: {
      onSuccess: (_data, variables) => {
        toast.success(t('common.messages.delete-success', { name: t('ai.chat-history.title') }));
        queryClient.invalidateQueries({
          queryKey: getListAgentChatThreadsQueryKey(projectId),
        });
        queryClient.removeQueries({
          queryKey: getGetAgentChatThreadQueryKey(projectId, variables.threadId),
        });
        invalidateRelatedMemos();
        // 削除したスレッドがサイドパネルでアクティブな場合、aiContextMenu の threadId が
        // 残ったままだと `useGetAgentChatThread` が再フェッチして 404 トーストが出る。
        // パネルを welcome 画面に戻して threadId をクリアする。
        const panel = useRouteSidePanelStore.getState();
        if (
          panel.mode?.type === 'ai-chat' &&
          panel.mode.context?.route === 'projectDetail' &&
          panel.mode.context.mode === 'agent_chat' &&
          panel.mode.context.threadId === variables.threadId
        ) {
          panel.setOpen({ isOpen: panel.isOpen, mode: { type: 'ai-chat' } });
        }
        onDeleteSuccess?.(variables.threadId);
      },
      onError: () => {
        toast.error(t('common.messages.delete-failed', { name: t('ai.chat-history.title') }));
      },
    },
  });

  const updateMutation = useUpdateAgentChatThread({
    mutation: {
      onSuccess: (_data, variables) => {
        toast.success(t('common.messages.update-success', { name: t('ai.chat-history.title') }));
        queryClient.invalidateQueries({
          queryKey: getListAgentChatThreadsQueryKey(projectId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetAgentChatThreadQueryKey(projectId, variables.threadId),
        });
        invalidateRelatedMemos();
      },
      onError: () => {
        toast.error(t('common.messages.update-failed', { name: t('ai.chat-history.title') }));
      },
    },
  });

  const favoriteMutation = useToggleAgentChatThreadFavorite({
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: getListAgentChatThreadsQueryKey(projectId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetAgentChatThreadQueryKey(projectId, variables.threadId),
        });
      },
      onError: () => {
        toast.error(t('common.messages.update-failed', { name: t('ai.chat-history.title') }));
      },
    },
  });

  const openRename = (thread: ThreadRef) => {
    setThreadToRename(thread);
    setRenameValue(thread.title);
  };

  const openDelete = (thread: ThreadRef) => {
    setThreadToDelete(thread);
  };

  const toggleFavorite = (thread: { id: string; is_favorite: boolean }) => {
    favoriteMutation.mutate({
      projectId,
      threadId: thread.id,
      data: { is_favorite: !thread.is_favorite },
    });
  };

  const copyUrl = async (threadId: string) => {
    const url = `${window.location.origin}/projects/${projectId}?threadId=${threadId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('ai.chat.copy-url-success'));
    } catch {
      toast.error(t('ai.chat.copy-url-failed'));
    }
  };

  const handleDeleteConfirm = () => {
    if (threadToDelete) {
      deleteMutation.mutate({ projectId, threadId: threadToDelete.id });
      setThreadToDelete(null);
    }
  };

  const handleRenameConfirm = () => {
    if (threadToRename && renameValue.trim()) {
      updateMutation.mutate({
        projectId,
        threadId: threadToRename.id,
        data: { title: renameValue.trim() },
      });
      setThreadToRename(null);
      setRenameValue('');
    }
  };

  const dialogs = (
    <>
      <Dialog
        open={threadToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setThreadToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        confirmVariant="destructive"
        confirmLabel={t('common.actions.delete')}
        cancelLabel={t('common.actions.cancel')}
        title={t('common.messages.delete-confirm-title', {
          name: t('ai.chat-history.title'),
        })}
      >
        <Text>
          {t('ai.chat-history.delete-confirm-message', { title: threadToDelete?.title })}
          <br />
          {t('ai.chat-history.delete-warning-memo-link')}
          <br />
          {t('ai.chat-history.delete-warning-irreversible')}
        </Text>
      </Dialog>

      <Dialog
        open={threadToRename !== null}
        onOpenChange={(open) => {
          if (!open) {
            setThreadToRename(null);
            setRenameValue('');
          }
        }}
        onConfirm={handleRenameConfirm}
        confirmLabel={t('common.actions.save')}
        cancelLabel={t('common.actions.cancel')}
        disabled={!renameValue.trim()}
        title={t('ai.chat-history.rename-dialog-title')}
        width="s"
      >
        <Input
          autoComplete={false}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          placeholder={t('ai.chat-history.rename-dialog-placeholder')}
          maxLength={255}
        />
      </Dialog>
    </>
  );

  return { openRename, openDelete, toggleFavorite, copyUrl, dialogs };
};
